<template>
  <div class="voice-setting-page page">
    <div class="container p-5 h-full">
      <!-- 中央面板：热词设定/声音设置 -->
      <div class="center-panel w-full flex flex-col min-w-0">
        <div class="main_bg p-5 rounded-xl flex-1 flex flex-col overflow-y-auto custom-scrollbar">
          <!-- 名称输入框 -->
          <div class="mb-8">
            <div class="flex justify-between items-center mb-2">
              <label class="text-white text-base">{{ $t('voiceSettings.name') }}</label>
              <span class="text-gray-400 text-sm">{{ voiceForm.name.length }}/10</span>
            </div>
            <el-input
              v-model="voiceForm.name"
              :placeholder="$t('voiceSettings.namePlaceholder')"
              maxlength="10"
              class="voice-input"
              @input="handleNameInput"
            ></el-input>
            <div v-if="nameCheckError" class="text-red-500 text-sm mt-1">
              {{ nameCheckError }}
            </div>
          </div>

          <!-- 上传区域 -->
          <div class="mb-8">
            <el-upload
              ref="audioUploadRef"
              class="upload-audio-container"
              action=""
              drag
              :http-request="handleAudioUploadRequest"
              :show-file-list="false"
              :before-upload="beforeAudioUpload"
              accept="audio/*,.mp3,.wav"
              style="display: none;"
            >
            </el-upload>
            
            <div
              v-if="cloneForm.uploadedUrl"
              class="flex justify-between items-center rounded-xl content_bg p-6 upload-area"
              style="position: relative;"
            >
              <div class="flex items-center">
                <img
                  src="@/assets/img/file.png"
                  class="w-16 h-16 object-contain mr-2"
                  :alt="$t('voiceSettings.uploaded')"
                />
                <div class="text-white text-sm">{{ $t('voiceSettings.uploaded') }}</div>
              </div>
              <div
                class="border-2 border-gray-600 rounded-full pl-4 pr-4 pt-2 pb-2 flex items-center cursor-pointer"
                @click="handleReuploadAudio"
              >
                <img
                  src="@/assets/img/upload.png"
                  class="w-8 h-8 object-contain"
                  :alt="$t('voiceSettings.reupload')"
                />
                <div class="text-gray-300 text-sm ml-2">{{ $t('voiceSettings.reupload') }}</div>
              </div>
              <el-progress
                v-if="audioUploading"
                :percentage="audioUploadProgress"
                :status="audioUploadProgress === 100 ? 'success' : null"
                :stroke-width="6"
                style="width: 200px; position: absolute; bottom: 10px; right: 10px;"
              ></el-progress>
            </div>
            
            <div
              v-else
              class="flex flex-col items-center justify-center rounded-xl content_bg p-6 upload-area cursor-pointer border-2 border-dashed border-gray-600"
              @click="handleClickUpload"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop"
            >
              <img style="height: 60px; width: 60px" src="@/assets/img/upload.png" mode="aspectFill" />
              <div class="text-white text-sm mt-2">{{ $t('voiceSettings.uploadHint') }}</div>
              <div class="text-gray-400 text-xs mt-1">{{ $t('voiceSettings.uploadFormatHint') }}</div>
              <div v-if="audioUploading" class="mt-3 w-full">
                <el-progress
                  :percentage="audioUploadProgress"
                  :status="audioUploadProgress === 100 ? 'success' : null"
                  :stroke-width="6"
                ></el-progress>
              </div>
            </div>
          </div>

          <!-- 音调滑块 -->
          <div class="mb-8">
            <label class="text-white text-base block ">{{ $t('voiceSettings.pitchRateLabel') }}</label>
            <div class="relative slider-container">
              <el-slider
                v-model="voiceForm.pitchRate"
                :min="0.5"
                :max="1.5"
                :step="0.1"
                :show-tooltip="false"
                class="pitch-slider"
                @input="updatePitchButton"
              ></el-slider>
            </div>
            <div class="flex justify-between mt-1">
              <span class="text-gray-400 text-xs">0.5</span>
              <span class="text-gray-400 text-xs">1.5</span>
            </div>
          </div>

          <!-- 语速滑块 -->
          <div class="mb-8">
            <label class="text-white text-base block">{{ $t('voiceSettings.speechRateLabel') }}</label>
            <div class="relative slider-container">
              <el-slider
                v-model="voiceForm.speechRate"
                :min="0.5"
                :max="1.5"
                :step="0.1"
                :show-tooltip="false"
                class="speech-slider"
                @input="updateSpeechRateButton"
              ></el-slider>
            </div>
            <div class="flex justify-between mt-1">
              <span class="text-gray-400 text-xs">0.5x</span>
              <span class="text-gray-400 text-xs">1.5x</span>
            </div>
          </div>

          <!-- 语言选择 -->
          <div class="mb-12">
            <label class="text-white text-base block mb-2">{{ $t('createRole.language') }}</label>
            <el-select
              v-model="voiceForm.language"
              :placeholder="$t('createRole.selectLanguage')"
              class="bg-[#2A2A3F] border-gray-700 text-white w-full"
              popper-class="custom-select-dropdown"
            >
              <el-option 
                v-for="lang in languageOptions" 
                :key="lang.value" 
                :label="lang.label" 
                :value="lang.value"
              ></el-option>
            </el-select>
          </div>


          <!-- 操作按钮 -->
          <div class="flex gap-3 mt-auto">
            <el-button
              type="primary"
              class="flex-1 bg-purple-600 hover:bg-purple-700 border-none h-12 rounded-xl"
              :loading="savingVoice"
              @click="saveVoiceToLibrary"
            >
              {{ $t('voiceSettings.saveToLibrary') }}
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import voiceApi from '@/network/voice/index'

export default {
  name: 'VoiceSetting',
  data() {
    return {
      // 中央面板表单
      voiceForm: {
        name: '',
        pitchRate: 1.0,
        speechRate: 1.0,
        language: '' // 语言选择
      },
      // 上传表单
      cloneForm: {
        uploadedUrl: ''
      },
      // 音频相关
      audioUploading: false,
      audioUploadProgress: 0,
      savingVoice: false,
      // 名称检查相关
      nameCheckTimer: null,
      nameCheckError: ''
    }
  },
  mounted() {
    this.updatePitchButton()
    this.updateSpeechRateButton()
  },
  beforeDestroy() {
    // 清理定时器
    if (this.nameCheckTimer) {
      clearTimeout(this.nameCheckTimer)
    }
  },
  methods: {
    // 处理名称输入
    handleNameInput() {
      // 清除之前的错误信息
      this.nameCheckError = ''
      
      // 清除之前的定时器
      if (this.nameCheckTimer) {
        clearTimeout(this.nameCheckTimer)
      }
      
      const name = this.voiceForm.name.trim()
      
      // 如果名称为空，不检查
      if (!name) {
        return
      }
      
      // 防抖：500ms 后执行检查
      this.nameCheckTimer = setTimeout(() => {
        this.checkVoiceName()
      }, 500)
    },
    // 检查声音名称是否重复
    async checkVoiceName() {
      const name = this.voiceForm.name.trim()
      
      // 如果名称为空，不检查
      if (!name) {
        this.nameCheckError = ''
        return
      }
      
      // 如果名称长度超过限制，不检查（会在保存时验证）
      if (name.length > 10) {
        this.nameCheckError = ''
        return
      }
      
      try {
        const res = await voiceApi.checkVoiceName({
          voiceName: name
        })
        
        // 接口返回格式：ResponseDTO<Boolean>，data 为 true 表示重复，false 表示不重复
        if (res.statusCode === 100200) {
          if (res.data === true) {
            this.nameCheckError = this.$t('voiceSettings.nameExists')
          } else {
            this.nameCheckError = ''
          }
        }
      } catch (error) {
        // 网络错误等，不显示错误（避免干扰用户体验）
        this.nameCheckError = ''
      }
    },
    // 更新音调滑块按钮显示
    updatePitchButton() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.pitch-slider')
        if (container) {
          const button = container.querySelector('.el-slider__button')
          if (button) {
            button.innerHTML = ''
            const text = document.createElement('span')
            text.textContent = this.voiceForm.pitchRate.toFixed(1)
            text.style.cssText = `
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 12px;
              font-weight: bold;
              pointer-events: none;
            `
            button.appendChild(text)
          }
        }
      })
    },
    // 更新语速滑块按钮显示
    updateSpeechRateButton() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.speech-slider')
        if (container) {
          const button = container.querySelector('.el-slider__button')
          if (button) {
            button.innerHTML = ''
            const text = document.createElement('span')
            text.textContent = this.voiceForm.speechRate.toFixed(1) + 'x'
            text.style.cssText = `
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 12px;
              font-weight: bold;
              pointer-events: none;
            `
            button.appendChild(text)
          }
        }
      })
    },
    // 保存声音到库（中央面板）
    async saveVoiceToLibrary() {
      if (!this.voiceForm.name || !this.voiceForm.name.trim()) {
        this.$message.warning(this.$t('voiceSettings.nameRequired'))
        return
      }
      if (this.voiceForm.name.length > 10) {
        this.$message.warning(this.$t('voiceSettings.nameMaxLength'))
        return
      }

      if (!this.cloneForm.uploadedUrl) {
        this.$message.warning(this.$t('voiceSettings.audioFileRequiredForClone'))
        return
      }

      if (!this.voiceForm.language) {
        this.$message.warning(this.$t('createRole.languageRequired'))
        return
      }

      this.savingVoice = true
      try {
        const locale = this.$i18n.locale || ''
        const groupId = String(locale).startsWith('zh') ? 1 : 2

        const res = await voiceApi.createVoice({
          voiceName: this.voiceForm.name.trim(),
          fileUrl: this.cloneForm.uploadedUrl,
          speechRate: this.voiceForm.speechRate,
          pitchRate: this.voiceForm.pitchRate,
          language: this.voiceForm.language,
          groupId: groupId
        })

        if (res.statusCode === 100200) {
          this.$message.success(this.$t('voiceSettings.saveSuccess'))
          // 重置表单
          this.voiceForm.name = ''
          this.voiceForm.pitchRate = 1.0
          this.voiceForm.speechRate = 1.0
          this.voiceForm.language = ''
          this.cloneForm.uploadedUrl = ''
        } else {
          this.$message.error(res.message || this.$t('voiceSettings.saveFailed'))
        }
      } catch (error) {
        const errorMsg = error.message || error.response?.data?.message || this.$t('common.unknownError')
        this.$message.error(this.$t('voiceSettings.saveFailed') + ': ' + errorMsg)
      } finally {
        this.savingVoice = false
      }
    },
    // 上传音频前验证
    beforeAudioUpload(file) {
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav']
      const validExtensions = ['.mp3', '.wav']
      
      const isValidType = validTypes.includes(file.type) || validExtensions.some(ext =>
        file.name.toLowerCase().endsWith(ext)
      )
      
      if (!isValidType) {
        this.$message.error(this.$t('voiceSettings.audioFormatError'))
        return false
      }
      
      const maxSize = 50 * 1024 * 1024 // 50MB
      if (file.size > maxSize) {
        this.$message.error(this.$t('voiceSettings.audioSizeError'))
        return false
      }
      
      return true
    },
    // 处理音频上传
    async handleAudioUploadRequest(option) {
      const file = option.file
      this.audioUploading = true
      this.audioUploadProgress = 0

      try {
        const res = await voiceApi.uploadAudio(file, (percent) => {
          this.audioUploadProgress = percent
        })

        this.audioUploading = false
        if (res.statusCode === 100200) {
          this.$message.success(this.$t('voiceSettings.uploadSuccess'))
          this.cloneForm.uploadedUrl = res.data?.url || res.data
          setTimeout(() => {
            this.audioUploadProgress = 0
          }, 1000)
        } else {
          this.$message.error(res.message || this.$t('voiceSettings.uploadFailed'))
          this.audioUploadProgress = 0
        }
      } catch (error) {
        this.audioUploading = false
        this.audioUploadProgress = 0
        const errorMsg = error.message || error.response?.data?.message || this.$t('common.unknownError')
        this.$message.error(this.$t('voiceSettings.uploadFailed') + ': ' + errorMsg)
      }
    },
    // 点击上传
    handleClickUpload() {
      const uploadInput = this.$refs.audioUploadRef?.$el?.querySelector('input[type="file"]')
      if (uploadInput) {
        uploadInput.click()
      }
    },
    // 重新上传音频
    handleReuploadAudio() {
      const input = this.$refs.audioUploadRef?.$el?.querySelector('input[type="file"]')
      if (input) {
        input.click()
      }
    },
    // 拖拽相关
    handleDragOver(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    handleDragLeave(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    handleDrop(e) {
      e.preventDefault()
      e.stopPropagation()
      
      const files = e.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        if (this.beforeAudioUpload(file)) {
          this.handleAudioUploadRequest({ file })
        }
      }
    },
  },
  computed: {
    languageOptions() {
      // 根据当前语言环境动态生成语言选项列表，与创建新人物页面保持一致
      return [
        { label: this.$t('createRole.languageChinese'), value: 'zh' },
        { label: this.$t('createRole.languageJapanese'), value: 'ja' },
        { label: this.$t('createRole.languageKorean'), value: 'ko' },
        { label: this.$t('createRole.languageEnglish'), value: 'en' },
        { label: this.$t('createRole.languageFrench'), value: 'fr' },
        { label: this.$t('createRole.languageSpanish'), value: 'es' },
        { label: this.$t('createRole.languageItalian'), value: 'it' },
        { label: this.$t('createRole.languageGerman'), value: 'de' }
      ]
    }
  }
}
</script>

<style scoped>
.voice-setting-page {
  min-height: calc(100vh - 84px);
}

.container {
  max-width: 100%;
  height: 100%;
}

.center-panel {
  min-width: 0;
}

/* 输入框样式 */
.voice-input :deep(.el-input__inner) {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
  color: #fff !important;
  border-radius: 12px;
  font-size: 16px;
  height: 50px;
}

/* 下拉框样式 */
.el-select :deep(.el-input__inner) {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
  color: #fff !important;
  border-radius: 12px;
  font-size: 16px;
  height: 50px;
}

/* 下拉框箭头颜色 */
.el-select :deep(.el-input__suffix .el-select__caret),
.el-select :deep(.el-input__suffix .el-icon-arrow-up),
.el-select :deep(.el-input__suffix .el-icon-arrow-down) {
  color: #ccc !important;
}

.el-select:hover :deep(.el-input__suffix .el-select__caret),
.el-select:hover :deep(.el-input__suffix .el-icon-arrow-up),
.el-select:hover :deep(.el-input__suffix .el-icon-arrow-down) {
  color: #fff !important;
}

/* 滑块样式 */
.pitch-slider,
.speech-slider {
  margin: 0;
}

.pitch-slider :deep(.el-slider__runway),
.speech-slider :deep(.el-slider__runway) {
  height: 12px !important;
  background-color: #404761 !important;
  border-radius: 6px;
}

.pitch-slider :deep(.el-slider__bar),
.speech-slider :deep(.el-slider__bar) {
  height: 12px !important;
  background-color: #713CF4 !important;
  border-radius: 3px;
}

.pitch-slider :deep(.el-slider__button),
.speech-slider :deep(.el-slider__button) {
  width: 24px !important;
  height: 24px !important;
  background-color: #713CF4 !important;
  border: 2px solid white !important;
  border-radius: 50% !important;
  position: relative !important;
  /* 清除默认的 ::after 内容（tooltip） */
}

/* 隐藏默认 tooltip */
.pitch-slider :deep(.el-slider__tooltip),
.speech-slider :deep(.el-slider__tooltip) {
  display: none !important;
}

/* 滑块容器 */
.slider-container {
  padding-top: 4px;
  margin-bottom: 5px;
}



/* 上传区域样式 */
.upload-audio-container {
  width: 100%;
}

.upload-area {
  cursor: pointer;
  transition: all 0.3s;
  min-height: 150px;
}

.upload-area:hover {
  opacity: 0.8;
}


/* 自定义滚动条 */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #4A4A6A;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #6A33F0;
}


</style>

<style>
/* 非 scoped 样式，用于覆盖 Element UI 下拉菜单（挂载在 body 上） */
.custom-select-dropdown {
  background-color: #2A2A3F !important;
  border-color: #4A4A6A !important;
}

.custom-select-dropdown .el-select-dropdown__item {
  color: #ccc !important;
  background-color: transparent !important;
}

.custom-select-dropdown .el-select-dropdown__item:hover,
.custom-select-dropdown .el-select-dropdown__item.hover {
  background-color: #383850 !important;
  color: #fff !important;
}

.custom-select-dropdown .el-select-dropdown__item.selected,
.custom-select-dropdown .el-select-dropdown__item.is-selected {
  color: #6A33F0 !important;
  background-color: #383850 !important;
}

.custom-select-dropdown .el-select-dropdown__item.selected:hover,
.custom-select-dropdown .el-select-dropdown__item.is-selected:hover {
  background-color: #404761 !important;
  color: #6A33F0 !important;
}
</style>
